// Calendar day placement (support #279162, dates).
//
// start_at / end_at are stored as UTC instants. Which calendar day(s) an event is
// on depends on the EVENT'S OWN timezone, not on the UTC date: an all-day UK event
// on Sunday 18 Oct (BST) is stored as 2026-10-17T23:00Z, and a 9am Sydney start is
// still the previous day in UTC. Taking the date straight off the UTC string put
// such events on the wrong day (and multi-day events on an extra or missing day).
// Event cards already format times in the event timezone; this matches them.

// Only used if an event has no valid timezone stored.
const COUNTRY_TZ = {
  GB: "Europe/London",
  IE: "Europe/Dublin",
  US: "America/New_York",
  AU: "Australia/Sydney",
  NZ: "Pacific/Auckland",
};

const formatters = new Map();
function formatterFor(tz) {
  if (!formatters.has(tz)) {
    let f = null;
    try {
      f = new Intl.DateTimeFormat("en-CA", {
        timeZone: tz,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      });
    } catch {
      f = null; // unknown timezone name
    }
    formatters.set(tz, f);
  }
  return formatters.get(tz);
}

// Local calendar date (YYYY-MM-DD) of a UTC instant in `tz`, and whether that local
// time is exactly midnight.
function localDate(iso, f) {
  const p = {};
  f.formatToParts(new Date(iso)).forEach(({ type, value }) => (p[type] = value));
  return {
    ymd: `${p.year}-${p.month}-${p.day}`,
    midnight: p.hour === "00" && p.minute === "00" && p.second === "00",
  };
}

function previousDay(ymd) {
  const d = new Date(ymd + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

// [firstDay, lastDay] (YYYY-MM-DD, inclusive) an occurrence covers in the event's
// timezone, or null without a start. An end at exactly local midnight belongs to
// the day before (a 9am–midnight event is one day, not two).
export function occurrenceDays(occ, timezone, country) {
  if (!occ?.start_at) return null;
  const f =
    formatterFor(timezone || "") ||
    formatterFor(COUNTRY_TZ[(country || "").toUpperCase()] || "") ||
    formatterFor("UTC");
  const first = localDate(occ.start_at, f).ymd;
  if (!occ.end_at) return [first, first];
  const end = localDate(occ.end_at, f);
  let last = end.ymd;
  if (end.midnight && last > first) last = previousDay(last);
  return [first, last < first ? first : last];
}

// Today's date on the viewer's own clock (the Calendar's "Today"), not in UTC —
// otherwise Australia / New Zealand mornings showed yesterday as today.
export function localTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
